import { type Dictionary, type Plural, ptBR } from './pt-BR.js';

export {
  permissionTexts,
  ptBR,
  type Dictionary,
  type Plural,
} from './pt-BR.js';

export type Vars = Record<string, string | number>;
export type Translate = (key: string, vars?: Vars) => string;

function lookup(dictionary: Dictionary, key: string): unknown {
  return key
    .split('.')
    .reduce<unknown>(
      (node, part) =>
        node && typeof node === 'object'
          ? (node as Record<string, unknown>)[part]
          : undefined,
      dictionary,
    );
}

function fill(template: string, vars: Vars): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    vars[name] === undefined ? whole : String(vars[name]),
  );
}

function isPlural(value: unknown): value is Plural {
  return (
    !!value && typeof value === 'object' && 'one' in value && 'other' in value
  );
}

/**
 * Singular ou plural. O Hermes (React Native) não tem `Intl.PluralRules` no
 * iOS: aí vale a regra do CLDR (português: 0 e 1 são singular).
 */
function pluralRule(locale: string): (count: number) => 'one' | 'other' {
  if (typeof Intl !== 'undefined' && typeof Intl.PluralRules === 'function') {
    const rules = new Intl.PluralRules(locale);
    return (count) => (rules.select(count) === 'one' ? 'one' : 'other');
  }
  const portuguese = locale.toLowerCase().startsWith('pt');
  return (count) => {
    const whole = Math.trunc(Math.abs(count));
    if (portuguese) return whole <= 1 ? 'one' : 'other';
    return count === 1 ? 'one' : 'other';
  };
}

/**
 * `t('compositor.placeholder', {canal: 'geral'})`. Plural pela variável `n`
 * (`Intl.PluralRules`). Chave que falta volta como a própria chave, para
 * aparecer na tela e ser corrigida.
 */
export function createTranslator(
  dictionary: Dictionary = ptBR,
  locale = 'pt-BR',
): Translate {
  const plural = pluralRule(locale);
  return (key, vars = {}) => {
    const value = lookup(dictionary, key);
    if (typeof value === 'string') return fill(value, vars);
    if (isPlural(value)) {
      const count = Number(vars.n ?? 0);
      return fill(plural(count) === 'one' ? value.one : value.other, vars);
    }
    return key;
  };
}

/** Tradutor padrão (pt-BR). */
export const t: Translate = createTranslator();
